import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  useGetHeadlineData,
  useGetHeadlineDataByCases,
  useGetMonitors
} from '../../../api/report-API'
import { SelectOption } from '../../../types/types'
import ProjectRunSelector, {
  PLOT_FILTER_SELECTION_KEY,
  PLOT_PROJECT_RUN_SELECTION_KEY
} from '../ProjectRunSelector'
import ChartContainer from './ChartContainer'
import type { RunCase } from './types'

const PLOT_ARRAY_STORAGE_KEY_PREFIX = 'PlotArray_'
const PLOT_YAXIS_MONITORS_STORAGE_KEY_PREFIX = 'PlotYAxisMonitors_'
const PLOT_SELECTED_CASES_STORAGE_KEY_PREFIX = 'PlotSelectedCases_'
const PLOT_PANEL_WIDTH_STORAGE_KEY_PREFIX = 'PlotPanelWidth_'
const PLOT_COL_WIDTHS_STORAGE_KEY_PREFIX = 'PlotColWidths_'
const PLOT_CELL_HEIGHTS_STORAGE_KEY_PREFIX = 'PlotCellHeights_'
const KEY_DELIMITER = '::'

const readSessionJson = (key: string, fallback: any) => {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) {
      return fallback
    }
    sessionStorage.removeItem(key)
    return JSON.parse(raw)
  } catch (error) {
    console.error(`Failed to parse session storage for ${key}:`, error)
    return fallback
  }
}

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}

const getCasesKey = (cases: RunCase[]) =>
  cases.map(c => `${c.gmId}:${c.stateName}:${c.baselineRun || ''}`).join('|')

const databricksRowsToObjects = (data: any) => {
  if (!data?.manifest?.schema?.columns || !data?.result?.data_array) {
    return []
  }

  const ts = Date.now()
  const cols = data.manifest.schema.columns
  return data.result.data_array.map((row, index) => {
    const rowObj = {
      index: ts + index
    } as Record<string, any>
    cols.forEach(col => {
      rowObj[col.name] = row[col.position]
    })
    return rowObj
  })
}

const PlotTabContainer = ({ plotIndex, plotKey }: { plotIndex: number; plotKey: string }) => {
  console.log('Initializing PlotTabContainer with plotKey: ', plotKey, 'and plotIndex: ', plotIndex)
  const [chartArray, setChartArray] = useState(() =>
    localStorage.getItem(`${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`)
      ? JSON.parse(localStorage.getItem(`${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`))
      : [[{}]]
  )
  const [, getMonitors] = useGetMonitors()
  const [xMonitors] = useState<SelectOption[]>([
    { label: 'Iteration', value: 'iteration' },
    { label: 'Physical Time', value: 'physical_time_s' }
  ])
  const [yMonitors, setYMonitors] = useState<SelectOption[]>(() =>
    localStorage.getItem(`${PLOT_YAXIS_MONITORS_STORAGE_KEY_PREFIX}${plotKey}`)
      ? JSON.parse(localStorage.getItem(`${PLOT_YAXIS_MONITORS_STORAGE_KEY_PREFIX}${plotKey}`))
      : []
  )
  const [initialProjectRunSelection] = useState<Record<string, any> | null>(() =>
    readSessionJson(PLOT_PROJECT_RUN_SELECTION_KEY, null)
  )
  const [initialPlotFilters] = useState<{ states?: string[]; baselines?: string[] }>(() =>
    readSessionJson(PLOT_FILTER_SELECTION_KEY, {})
  )
  const [selectedCases, setSelectedCases] = useState<RunCase[]>([])
  const [runAverageData, setRunAverageData] = useState<Record<string, any>[]>([])
  const [rawHeadlineData, setRawHeadlineData] = useState<Record<string, any>[]>([])
  const [selectedProjects, setSelectedProjects] = useState<string[]>([])
  const [selectedRuns, setSelectedRuns] = useState<string[]>([])
  const [selectedStates, setSelectedStates] = useState<string[]>(initialPlotFilters.states || [])
  const [selectedBaselines, setSelectedBaselines] = useState<string[]>(
    initialPlotFilters.baselines || []
  )
  const [selectorLoading, setSelectorLoading] = useState(false)
  const [headlineDataLoading, setHeadlineDataLoading] = useState(false)
  const [chartLoadingArray, setChartLoadingArray] = useState<Map<string, boolean>>(new Map())
  const loading = useMemo(() => {
    return chartLoadingArray.values().some(value => value) || selectorLoading || headlineDataLoading
  }, [chartLoadingArray, selectorLoading, headlineDataLoading])
  const refCases = useRef<RunCase[]>([])
  const refRunAverageProjectsKey = useRef('')
  const refCasesKey = useRef<string | null>(null)
  const refLoadedProjectsKey = useRef('')
  const refLoadingProjectsKey = useRef('')

  const [largerChartRow, setLargerChartRow] = useState<number | undefined>(undefined)
  const [largerChartCol, setLargerChartCol] = useState<number | undefined>(undefined)

  // Resizable panel state
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const stored = localStorage.getItem(`${PLOT_PANEL_WIDTH_STORAGE_KEY_PREFIX}${plotKey}`)
    return stored ? parseInt(stored, 10) : 300
  })
  // Column width ratios per row
  const [colWidths, setColWidths] = useState<Record<number, number[]>>(() => {
    const stored = localStorage.getItem(`${PLOT_COL_WIDTHS_STORAGE_KEY_PREFIX}${plotKey}`)
    return stored ? JSON.parse(stored) : {}
  })
  // Per-cell heights (key: "rowIndex-colIndex" → pixel height)
  const [cellHeights, setCellHeights] = useState<Record<string, number>>(() => {
    const stored = localStorage.getItem(`${PLOT_CELL_HEIGHTS_STORAGE_KEY_PREFIX}${plotKey}`)
    return stored ? JSON.parse(stored) : {}
  })

  const isDragging = useRef(false)
  const dragType = useRef<'panel' | 'col' | 'cell-height'>('panel')
  const dragIndex = useRef<{ row: number; col: number }>({ row: 0, col: 0 })
  const dragStartPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const dragStartSizes = useRef<number[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const chartAreaRef = useRef<HTMLDivElement>(null)

  const startDrag = useCallback(
    (type: 'panel' | 'col' | 'cell-height', e: React.MouseEvent, row = 0, col = 0) => {
      e.preventDefault()
      isDragging.current = true
      dragType.current = type
      dragIndex.current = { row, col }
      dragStartPos.current = { x: e.clientX, y: e.clientY }
      document.body.style.userSelect = 'none'
      if (type === 'panel' || type === 'col') {
        document.body.style.cursor = 'col-resize'
      } else {
        document.body.style.cursor = 'row-resize'
      }
    },
    []
  )

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return

      if (dragType.current === 'panel' && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const newWidth = Math.min(Math.max(e.clientX - containerRect.left, 200), 600)
        setPanelWidth(newWidth)
      } else if (dragType.current === 'col' && chartAreaRef.current) {
        // Resize columns within a row
        const { row, col } = dragIndex.current
        const rowEl = chartAreaRef.current.querySelectorAll<HTMLElement>('[data-chart-row]')[row]
        if (!rowEl) return
        const rowRect = rowEl.getBoundingClientRect()
        const rowWidth = rowRect.width
        const colCount = chartArray[row]?.length || 1
        const currentWidths = colWidths[row] || Array(colCount).fill(100 / colCount)
        const relativeX = e.clientX - rowRect.left
        const percentAtCursor = (relativeX / rowWidth) * 100
        const sumBefore = currentWidths.slice(0, col).reduce((a, b) => a + b, 0)
        const sumPair = currentWidths[col] + currentWidths[col + 1]
        const newLeft = Math.max(10, Math.min(percentAtCursor - sumBefore, sumPair - 10))
        const newRight = sumPair - newLeft
        const newWidths = [...currentWidths]
        newWidths[col] = newLeft
        newWidths[col + 1] = newRight
        setColWidths(prev => ({ ...prev, [row]: newWidths }))
      } else if (dragType.current === 'cell-height') {
        // Resize individual chart cell height
        const deltaY = e.clientY - dragStartPos.current.y
        const startHeight = dragStartSizes.current[0] || 400
        const newHeight = Math.max(200, startHeight + deltaY)
        const key = `${dragIndex.current.row}-${dragIndex.current.col}`
        setCellHeights(prev => ({ ...prev, [key]: newHeight }))
      }
    }
    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        // Persist all sizes
        localStorage.setItem(`${PLOT_PANEL_WIDTH_STORAGE_KEY_PREFIX}${plotKey}`, String(panelWidth))
        localStorage.setItem(
          `${PLOT_COL_WIDTHS_STORAGE_KEY_PREFIX}${plotKey}`,
          JSON.stringify(colWidths)
        )
        localStorage.setItem(
          `${PLOT_CELL_HEIGHTS_STORAGE_KEY_PREFIX}${plotKey}`,
          JSON.stringify(cellHeights)
        )
      }
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [panelWidth, colWidths, cellHeights, plotKey, chartArray])

  const [, getHeadlineData] = useGetHeadlineData()
  const [, getHeadlineDataByCases] = useGetHeadlineDataByCases()
  const [trendsData, setTrendsData] = useState<Record<string, any>[]>([])

  const loadRunAverageData = useCallback(
    (cases: RunCase[]) => {
      const projects = Array.from(
        new Set(cases.map(c => c.project).filter((project): project is string => Boolean(project)))
      )
      const projectsKey = projects.join('|')

      if (projects.length === 0) {
        refRunAverageProjectsKey.current = ''
        setRunAverageData([])
        return
      }

      if (projectsKey === refRunAverageProjectsKey.current) {
        return
      }

      refRunAverageProjectsKey.current = projectsKey
      getHeadlineData({
        page: 0,
        pageSize: 10000,
        projects: projects.join(',')
      } as any)
        .then(res => {
          if (projectsKey !== refRunAverageProjectsKey.current) {
            return
          }
          setRunAverageData(databricksRowsToObjects(res.data))
        })
        .catch(error => {
          console.error('Error fetching run average headline data:', error)
          toast.error('Failed to fetch run average data. Please try again.')
          if (projectsKey === refRunAverageProjectsKey.current) {
            refRunAverageProjectsKey.current = ''
          }
          setRunAverageData([])
        })
    },
    [getHeadlineData]
  )

  const handleSelectedCasesChange = useCallback(
    (cases: RunCase[]) => {
      const nextCasesKey = getCasesKey(cases)
      if (nextCasesKey === refCasesKey.current) {
        return
      }

      refCasesKey.current = nextCasesKey
      refCases.current = cases
      setSelectedCases(cases)
      console.log('Selected cases:', cases)
      localStorage.setItem(
        `${PLOT_SELECTED_CASES_STORAGE_KEY_PREFIX}${plotKey}`,
        JSON.stringify(cases)
      )

      if (cases.length === 0) {
        setTrendsData([])
        setRunAverageData([])
        setYMonitors([])
        return
      }

      loadRunAverageData(cases)

      getMonitors({ cases: cases.map(c => c.gmId).join(',') } as any)
        .then(res => {
          const monitors = res.data as any
          const monitorOptions = monitors.map(monitor => {
            const name = monitor.monitor_name_clean
            const label = name.replace(/_Monitor$/, '').replace(/ Monitor$/, '')
            return {
              label,
              value: name
            } as SelectOption
          })
          setYMonitors(monitorOptions)
          localStorage.setItem(
            `${PLOT_YAXIS_MONITORS_STORAGE_KEY_PREFIX}${plotKey}`,
            JSON.stringify(monitorOptions)
          )
        })
        .catch(error => {
          console.error('Error fetching monitors:', error)
          toast.error('Failed to fetch monitors. Please try again.')
        })

      getHeadlineDataByCases({
        page: 0,
        pageSize: 10000,
        cases: cases.map(c => c.gmId).join(',')
      } as any)
        .then(res => {
          const data = res.data as any
          const ts = Date.now()
          console.log('Fetched headline data by cases:', data)
          const cols = data.manifest.schema.columns
          const dataArray = data.result.data_array.map((row, index) => {
            const rowObj = {
              index: ts + index
            } as Record<string, any>
            cols.forEach(col => {
              rowObj[col.name] = row[col.position]
            })
            return rowObj
          })
          setTrendsData(dataArray)
          console.log('Fetched trends data:', dataArray)
        })
        .catch(error => {
          console.error('Error fetching headline data by cases:', error)
          toast.error('Failed to fetch trends data. Please try again.')
        })
    },
    [getHeadlineDataByCases, getMonitors, loadRunAverageData, plotKey]
  )

  const loadPlotHeadlineData = useCallback(
    async (projects: string[]) => {
      const uniqueProjects = Array.from(new Set(projects.filter(Boolean)))
      const projectsKey = uniqueProjects.join('|')
      if (uniqueProjects.length === 0) {
        refLoadedProjectsKey.current = ''
        refLoadingProjectsKey.current = ''
        setRawHeadlineData([])
        return
      }

      if (
        projectsKey === refLoadedProjectsKey.current ||
        projectsKey === refLoadingProjectsKey.current
      ) {
        return
      }

      refLoadingProjectsKey.current = projectsKey
      setHeadlineDataLoading(true)
      try {
        const reportRes = (await getHeadlineData({
          page: 0,
          pageSize: 10000,
          projects: uniqueProjects.join(',')
        } as any)) as any
        const data = reportRes.data
        if (!data?.manifest?.schema?.columns || !data?.result?.data_array) {
          setRawHeadlineData([])
          return
        }

        const cols = data.manifest.schema.columns
        const ts = Date.now()
        const dataArray = data.result.data_array.map((row, index) => {
          const rowObj = {
            index: ts + index
          } as Record<string, any>
          cols.forEach(col => {
            rowObj[col.name] = row[col.position]
          })
          return rowObj
        })
        setRawHeadlineData(prevData => {
          const prevKey = prevData.map(row => String(row.gm_id || '')).join('|')
          const nextKey = dataArray.map(row => String(row.gm_id || '')).join('|')
          return prevKey === nextKey ? prevData : dataArray
        })
        refLoadedProjectsKey.current = projectsKey
      } catch (error) {
        console.error('Error fetching plot headline data:', error)
        toast.error('Error fetching headline data for plot filters')
        setRawHeadlineData([])
      } finally {
        if (refLoadingProjectsKey.current === projectsKey) {
          refLoadingProjectsKey.current = ''
        }
        setHeadlineDataLoading(false)
      }
    },
    [getHeadlineData]
  )

  const handleProjectRunSelectionChange = useCallback(
    (
      projectRunSelection: Record<string, boolean>,
      projects: string[],
      _projectSelectionChanged: boolean,
      runs: string[]
    ) => {
      setSelectedProjects(prevProjects =>
        areStringArraysEqual(prevProjects, projects) ? prevProjects : projects
      )
      setSelectedRuns(prevRuns => (areStringArraysEqual(prevRuns, runs) ? prevRuns : runs))
      loadPlotHeadlineData(projects)
    },
    [loadPlotHeadlineData]
  )

  const handleFilterLoadingChanged = useCallback((loadingFilter: boolean) => {
    setSelectorLoading(loadingFilter)
  }, [])

  const handleStateSelectionChanged = useCallback((states: string[]) => {
    setSelectedStates(prevStates =>
      areStringArraysEqual(prevStates, states) ? prevStates : states
    )
  }, [])

  const handleBaselineSelectionChanged = useCallback((baselines: string[]) => {
    setSelectedBaselines(prevBaselines =>
      areStringArraysEqual(prevBaselines, baselines) ? prevBaselines : baselines
    )
  }, [])

  const handleSelectorReset = useCallback(() => {
    setSelectedProjects(prevProjects => (prevProjects.length === 0 ? prevProjects : []))
    setSelectedRuns(prevRuns => (prevRuns.length === 0 ? prevRuns : []))
    setSelectedStates(prevStates => (prevStates.length === 0 ? prevStates : []))
    setSelectedBaselines(prevBaselines => (prevBaselines.length === 0 ? prevBaselines : []))
    setRawHeadlineData(prevData => (prevData.length === 0 ? prevData : []))
  }, [])

  useEffect(() => {
    const selectedProjectSet = new Set(selectedProjects.map(project => String(project)))
    const selectedRunSet = new Set(selectedRuns.map(run => String(run)))
    const selectedStateSet = new Set(selectedStates.map(state => String(state)))
    const selectedBaselineSet = new Set(selectedBaselines.map(baseline => String(baseline)))
    const hasRunFilter = selectedRunSet.size > 0
    const hasStateFilter = selectedStateSet.size > 0
    const hasBaselineFilter = selectedBaselineSet.size > 0
    const cases: RunCase[] = []
    const seenGmIds = new Set<string>()

    rawHeadlineData.forEach(row => {
      const project = String(row.project || '')
      const run = String(row.run || '')
      const gmId = String(row.gm_id || '')
      const stateName = String(row.state_name || '')
      const runKey = `${project}${KEY_DELIMITER}${run}`

      if (!project || !run || !gmId) return
      if (selectedProjectSet.size === 0 || !selectedProjectSet.has(project)) return
      if (hasRunFilter && !selectedRunSet.has(runKey)) return
      if (hasStateFilter && !selectedStateSet.has(stateName)) return
      if (hasBaselineFilter && !selectedBaselineSet.has(String(row.baseline_run))) return
      if (seenGmIds.has(gmId)) return

      seenGmIds.add(gmId)
      cases.push({
        gmId,
        stateName,
        baselineRun: row.baseline_run ? String(row.baseline_run) : undefined,
        project,
        run
      })
    })

    handleSelectedCasesChange(cases)
  }, [
    rawHeadlineData,
    selectedProjects,
    selectedRuns,
    selectedStates,
    selectedBaselines,
    handleSelectedCasesChange
  ])

  const addChartRight = (row, col) => {
    const newRow = [...chartArray[row]]
    if (newRow.length === col) {
      newRow.push({
        ts: Date.now()
      })
    } else {
      newRow.splice(col + 1, 0, { ts: Date.now() })
    }
    const newChartArray = [...chartArray]
    newChartArray[row] = newRow
    setChartArray(newChartArray)
    localStorage.setItem(
      `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
      JSON.stringify(newChartArray)
    )
  }

  const addChartBelow = (row, col) => {
    const newChartArray = [...chartArray]
    if (newChartArray.length === row) {
      newChartArray.push([{ ts: Date.now() }])
    } else {
      newChartArray.splice(row + 1, 0, [{ ts: Date.now() }])
    }
    setChartArray(newChartArray)
    localStorage.setItem(
      `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
      JSON.stringify(newChartArray)
    )
  }

  const removeChart = (row, col) => {
    if (chartArray.length === 1 && chartArray[row].length === 1) {
      return
    }
    const newChartArray = [...chartArray]
    newChartArray[row].splice(col, 1)
    if (newChartArray[row].length === 0) {
      newChartArray.splice(row, 1)
    }
    setChartArray(newChartArray)
    localStorage.setItem(
      `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
      JSON.stringify(newChartArray)
    )
  }

  return (
    <div ref={containerRef} className="flex min-h-full w-full items-stretch relative gap-2">
      {/* Collapse/Expand toggle */}
      {panelCollapsed ? (
        <button
          className="shrink-0 w-6 flex flex-col items-center justify-center hover:bg-blue-500/10 transition-colors border-r border-solid border-neutral-200 dark:border-neutral-600"
          onClick={() => setPanelCollapsed(false)}
          title="Expand panel">
          <i className="pi pi-chevron-right text-xs text-neutral-500 dark:text-neutral-400" />
        </button>
      ) : (
        <>
          <div
            className="flex flex-col items-stretch gap-3 shrink-0 overflow-x-auto"
            style={{ width: panelWidth, minWidth: panelWidth }}>
            <ProjectRunSelector
              loadingHeadlineData={loading}
              showFilter={true}
              storageScope={`plot:${plotKey}`}
              initialProjectRunSelection={initialProjectRunSelection}
              initialStates={initialPlotFilters.states || []}
              initialBaselines={initialPlotFilters.baselines || []}
              baselineSourceData={rawHeadlineData}
              onProjectRunSelectionChanged={handleProjectRunSelectionChange}
              onFilterLoadingChanged={handleFilterLoadingChanged}
              onStateSelectionChanged={handleStateSelectionChanged}
              onBaselineSelectionChanged={handleBaselineSelectionChanged}
              onReset={handleSelectorReset}
            />
          </div>
          {/* Resizable drag handle with collapse button */}
          <div
            className="w-2 flex flex-col items-center justify-center shrink-0 relative group"
            title="Drag to resize">
            <button
              className="absolute mr-4 top-2 p-0.5 rounded hover:bg-blue-500/20 text-neutral-500 dark:text-neutral-400 hover:text-gm-blue dark:hover:text-light-blue transition-colors z-10"
              onClick={() => setPanelCollapsed(true)}
              title="Collapse panel">
              <i className="pi pi-chevron-left text-[0.6rem]" />
            </button>
            <div
              className="w-full h-full flex items-center justify-center cursor-col-resize hover:bg-blue-500/20 active:bg-blue-500/30 transition-colors"
              onMouseDown={e => startDrag('panel', e)}>
              <div className="w-[2px] h-8 bg-neutral-400 dark:bg-neutral-500 rounded" />
            </div>
          </div>
        </>
      )}
      <div
        ref={chartAreaRef}
        className="flex flex-col grow items-stretch justify-start overflow-y-auto plot-scroll min-w-0"
        style={{ scrollbarWidth: 'thin' }}>
        {chartArray.map((row: any[], rowIndex) => {
          const colCount = row.length
          const widths = colWidths[rowIndex] || Array(colCount).fill(100 / colCount)
          return (
            <div key={`rowgroup-${plotKey}-${rowIndex}`} className="flex flex-col">
              <div
                data-chart-row
                key={`row-${plotKey}-${rowIndex}`}
                className="flex items-start w-full overflow-hidden">
                {row.map((col: any, colIndex) => {
                  const cellKey = `${rowIndex}-${colIndex}`
                  const cellHeight = cellHeights[cellKey] || 400
                  return (
                    <React.Fragment key={`colgroup-${plotKey}-${col.ts}-${rowIndex}-${colIndex}`}>
                      <div
                        className="flex flex-col"
                        style={{ flex: `${widths[colIndex]} 0 0%`, minWidth: '200px' }}>
                        <div
                          className="border border-solid rounded-xl overflow-hidden"
                          style={{ height: cellHeight }}>
                          <ChartContainer
                            key={`chart-${plotKey}-${col.ts}-${rowIndex}-${colIndex}`}
                            chartKey={`${plotKey}-${col.ts}-${rowIndex}-${colIndex}`}
                            chartDataType={col.chartDataType || 'monitors'}
                            xIndex={colIndex}
                            yIndex={rowIndex}
                            xMonitors={xMonitors}
                            yMonitors={yMonitors}
                            selectedCases={selectedCases}
                            defaultXAxis={col.xAxis}
                            defaultYAxis={col.yAxis}
                            defaultTrendsXAxis={col.trendsXAxis}
                            defaultTrendsYAxis={col.trendsYAxis}
                            defaultYScale={col.yScale || 'linear'}
                            defaultViewSettings={col.viewSettings}
                            defaultAverageMode={col.averageMode || 'filtered'}
                            showRollingAverage={col.showRollingAverage || false}
                            defaultWindowSize={col.windowSize || 50}
                            trendsData={trendsData}
                            runAverageData={runAverageData}
                            onXAxisChange={(row, col, xAxis) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], xAxis }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onYAxisChange={(row, col, yAxis) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], yAxis }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onYScaleChange={(row, col, yScale) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], yScale }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onViewSettingsChange={(row, col, viewSettings) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], viewSettings }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onTrendsXAxisChange={(row, col, xAxis) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], trendsXAxis: xAxis }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onTrendsYAxisChange={(row, col, yAxis) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], trendsYAxis: yAxis }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onAverageModeChange={(row, col, averageMode) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], averageMode }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onAddChartRight={addChartRight}
                            onAddChartBelow={addChartBelow}
                            onRemoveChart={removeChart}
                            onPlotingStatesChange={(row, col, plotting) => {
                              const key = `${row}-${col}`
                              setChartLoadingArray(prev => {
                                const newMap = new Map(prev)
                                newMap.set(key, plotting)
                                return newMap
                              })
                            }}
                            onRollingAverageChange={(row, col, showRollingAverage) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], showRollingAverage }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onChartDataTypeChange={(row, col, chartDataType) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], chartDataType }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onRollingAverageWindowSizeChange={(row, col, windowSize) => {
                              const newChartArray = [...chartArray]
                              const chart = { ...newChartArray[row][col], windowSize }
                              newChartArray[row][col] = chart
                              setChartArray(newChartArray)
                              localStorage.setItem(
                                `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                                JSON.stringify(newChartArray)
                              )
                            }}
                            onEnlargeChart={(row, col) => {
                              setLargerChartRow(row)
                              setLargerChartCol(col)
                            }}
                          />
                        </div>
                        {/* Per-cell bottom resize handle */}
                        <div
                          className="h-2 cursor-row-resize flex justify-center items-center hover:bg-blue-500/20 active:bg-blue-500/30 transition-colors"
                          onMouseDown={e => {
                            const key = `${rowIndex}-${colIndex}`
                            dragStartSizes.current = [cellHeights[key] || 400]
                            startDrag('cell-height', e, rowIndex, colIndex)
                          }}
                          title="Drag to resize chart height">
                          <div className="h-[2px] w-8 bg-neutral-400 dark:bg-neutral-500 rounded" />
                        </div>
                      </div>
                      {/* Column resize handle between cells */}
                      {colIndex < row.length - 1 && (
                        <div
                          className="w-2 cursor-col-resize flex items-center justify-center hover:bg-blue-500/20 active:bg-blue-500/30 transition-colors shrink-0"
                          onMouseDown={e => startDrag('col', e, rowIndex, colIndex)}
                          title="Drag to resize columns">
                          <div className="w-[2px] h-8 bg-neutral-400 dark:bg-neutral-500 rounded" />
                        </div>
                      )}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      {largerChartRow !== undefined &&
        largerChartCol !== undefined &&
        chartArray[largerChartRow] &&
        chartArray[largerChartRow][largerChartCol] && (
          <div className="z-20 absolute -top-1 -bottom-1 -left-4 -right-1 bg-white dark:bg-neutral-90 border border-solid rounded-xl p-2">
            <ChartContainer
              chartKey={`${plotKey}-${chartArray[largerChartRow][largerChartCol].ts}-${largerChartRow}-${largerChartCol}`}
              key={`large-chart-${plotKey}-${chartArray[largerChartRow][largerChartCol].ts}-${largerChartRow}-${largerChartCol}`}
              chartDataType={chartArray[largerChartRow][largerChartCol].chartDataType}
              isEnlarged={true}
              xIndex={largerChartCol}
              yIndex={largerChartRow}
              xMonitors={xMonitors}
              yMonitors={yMonitors}
              selectedCases={selectedCases}
              defaultXAxis={chartArray[largerChartRow][largerChartCol].xAxis}
              defaultYAxis={chartArray[largerChartRow][largerChartCol].yAxis}
              defaultYScale={chartArray[largerChartRow][largerChartCol].yScale || 'linear'}
              defaultViewSettings={chartArray[largerChartRow][largerChartCol].viewSettings}
              defaultAverageMode={
                chartArray[largerChartRow][largerChartCol].averageMode || 'filtered'
              }
              showRollingAverage={
                chartArray[largerChartRow][largerChartCol].showRollingAverage || false
              }
              defaultWindowSize={chartArray[largerChartRow][largerChartCol].windowSize || 50}
              defaultTrendsXAxis={chartArray[largerChartRow][largerChartCol].trendsXAxis}
              defaultTrendsYAxis={chartArray[largerChartRow][largerChartCol].trendsYAxis}
              trendsData={trendsData}
              runAverageData={runAverageData}
              onXAxisChange={(row, col, xAxis) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], xAxis }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onYAxisChange={(row, col, yAxis) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], yAxis }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onYScaleChange={(row, col, yScale) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], yScale }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onViewSettingsChange={(row, col, viewSettings) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], viewSettings }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onAddChartRight={addChartRight}
              onAddChartBelow={addChartBelow}
              onRemoveChart={() => {
                setLargerChartRow(undefined)
                setLargerChartCol(undefined)
              }}
              onTrendsXAxisChange={(row, col, xAxis) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], trendsXAxis: xAxis }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onTrendsYAxisChange={(row, col, yAxis) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], trendsYAxis: yAxis }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onAverageModeChange={(row, col, averageMode) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], averageMode }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onPlotingStatesChange={(row, col, plotting) => {
                const key = `${row}-${col}`
                setChartLoadingArray(prev => {
                  const newMap = new Map(prev)
                  newMap.set(key, plotting)
                  return newMap
                })
              }}
              onRollingAverageChange={(row, col, showRollingAverage) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], showRollingAverage }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onChartDataTypeChange={(row, col, chartDataType) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], chartDataType }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
              onRollingAverageWindowSizeChange={(row, col, windowSize) => {
                const newChartArray = [...chartArray]
                const chart = { ...newChartArray[row][col], windowSize }
                newChartArray[row][col] = chart
                setChartArray(newChartArray)
                localStorage.setItem(
                  `${PLOT_ARRAY_STORAGE_KEY_PREFIX}${plotKey}`,
                  JSON.stringify(newChartArray)
                )
              }}
            />
          </div>
        )}
    </div>
  )
}

export default PlotTabContainer
